import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { getInitiateIvSelector } from '../selectors'
import { chevronBackOutline } from 'ionicons/icons'
import {
  saveInitiateIv,
  setSite,
  setSitePNNV,
  setGauge,
  setType,
  setUpdateInputRows,
  setInitiateIvDefaults,
} from '../store/initiateIv.actions'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../../utilities'
import { InitiateIvConstants } from '../store/initiateIv.constants'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import { setInputRows, setIsFirstVisit } from '../../../../components/procedure/store/procedure.actions'
import Procedure from '../../../../components/procedure/Procedure.component'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { getInitiateIvOptionsSelector } from '../../../options/selectors'
import { IOption, IPNNVOption } from '../../../../common/common.types'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialInitiateIvState } from '../store/initiateIv.reducers'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'
import { getDefaultsSelector } from '../../../defaults/selectors/defaults.selector'
import { getAgeRangeGroupId, getDefaultsForAgeRange } from '../../../../utilities/procedures.util'
import { getPatientDemographicsSelector } from '../../../patientDemographics/selectors'
import { getProcedureStateSelector } from '../../../../components/procedure/selectors'
import { goBack } from '../../../../utilities/navigation.util'
import { IInitiateIvState } from '../store/initiateIv.types'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'

const InitiateIV: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const initiateIv: IInitiateIvState = useSelector(getInitiateIvSelector)
  const {
    date,
    time,
    assessedBy,
    successfulAttempt,
    pta,
    site,
    sitePNNV,
    size,
    iVType,
    attempts,
    procedureComplications,
    response,
    responsePNNV,
    authorization,
    comments,
    updateInputRows,
    rolePerforming,
    rolePerformingPNNV,
    isEkgImport,
    initiateIvDefaults,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
  } = initiateIv
  const initiateIvOptions = useSelector(getInitiateIvOptionsSelector)
  const { siteOptions, typeOptions } = initiateIvOptions
  const { defaults } = useSelector(getDefaultsSelector)
  const { dOB, age, ageUnit } = useSelector(getPatientDemographicsSelector)
  const { isFirstVisit } = useSelector(getProcedureStateSelector)
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS
  const save = () => {
    dispatch(saveInitiateIv())
  }

  const siteOnChange = (option: IOption) => {
    dispatch(setSite(option))
    dispatch(setUpdateInputRows(true))
    save()
  }

  const sitePNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    dispatch(setSitePNNV(pnnvOption))
    dispatch(setUpdateInputRows(true))
    save()
  }

  const typeOnChange = (option: IOption) => {
    dispatch(setType(option))
    save()
  }

  const gaugeOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setGauge(e.detail.value))
    save()
  }

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows', 'iVType', 'initiateIvDefaults']
    const currentState = filterOutObjectKeys(initiateIv, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialInitiateIvState, fieldsToFilter)

    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()

    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'initiateIv/SET_TIME', time: time })
              dispatch({ type: 'initiateIv/SET_DATE', date: date })
              save()
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: initiateIv.key,
                rowActionType: TimelineConstants.SET_INITIATE_IV_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const inputRows = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: InitiateIvConstants.SET_SITE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('initiateIv.site'),
            dataTestID: 'Site',
            options: siteOptions,
            action: siteOnChange,
            listHeader: 'Site',
            selectedOption: site,
            selectedPNNVOption: sitePNNV,
            pnnvAction: sitePNNVOnChange,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: InitiateIvConstants.SET_GAUGE,
          type: ProcedureConstants.TEXT,
          hidden: false,
          props: {
            label: t('initiateIv.gauge'),
            dataTestID: 'Gauge',
            inputValue: size,
            onChangeAction: gaugeOnChange,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: InitiateIvConstants.SET_TYPE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('initiateIv.type'),
            dataTestID: 'Type',
            options: typeOptions,
            action: typeOnChange,
            listHeader: 'Type',
            selectedOption: iVType,
          },
        },
      ],
    },
  ]

  const setIvTypeDefault = () => {
    const ivTypeDefault = typeOptions.find((option) => option.name === initiateIvDefaults?.defaultAction)

    if (ivTypeDefault) {
      dispatch(setType(ivTypeDefault))
      return
    }

    dispatch(setUpdateInputRows(true))
  }

  const setFieldDefaults = () => {
    const fieldDefaults = initiateIvDefaults?.defaults.filter((field) => field.actionId === iVType?.name)

    fieldDefaults?.forEach((fieldDefault) => {
      const fieldName = fieldDefault.fieldName
      const defaultValue = fieldDefault.defaultValue

      // Site
      if (fieldName === 'field1') {
        const siteOption = siteOptions.find((option) => option.id === defaultValue)
        dispatch(setSite(siteOption || null))
      }

      // Gauge
      else if (fieldName === 'field2') {
        dispatch(setGauge(defaultValue || ''))
      }
    })

    dispatch(setUpdateInputRows(true))
  }

  useIonViewWillEnter(() => {
    const ageRange = getAgeRangeGroupId(dOB, age, ageUnit)
    const defaultsForAgeRange = getDefaultsForAgeRange(defaults, 'Initiate IV', ageRange)

    if (defaultsForAgeRange) dispatch(setInitiateIvDefaults(defaultsForAgeRange))
    else dispatch(setUpdateInputRows(true))
  }, [defaults, dOB, age, ageUnit])

  useEffect(() => {
    if (!isFirstVisit || !initiateIvDefaults || iVType) return

    setIvTypeDefault()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiateIvDefaults])

  useEffect(() => {
    if (!isFirstVisit || !initiateIvDefaults || !iVType) return

    setFieldDefaults()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iVType, initiateIvDefaults])

  useIonViewDidLeave(() => {
    dispatch(setIsFirstVisit(false))
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage id='InitiateIvPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Initiate IV</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'initiateIv'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          successfulAttempt={successfulAttempt}
          procedureComplications={procedureComplications}
          response={response}
          responsePNNV={responsePNNV}
          authorization={authorization}
          comments={comments}
          attempts={attempts}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          pta={pta}
          isEkgImport={isEkgImport}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
        />
      </IonContent>
    </IonPage>
  )
}

export default InitiateIV
